import {
  Avatar,
  type AvatarProps,
  Box,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { SystemStyleObject } from "@mui/system/styleFunctionSx";
import { type ReactNode, forwardRef } from "react";

import type { AppAvatarSize } from "./appAvatar.types.ts";

type BadgeConfig = {
  avatar: SystemStyleObject<Theme>;
  image: SystemStyleObject<Theme>;
  status: SystemStyleObject<Theme>;
};

type AvatarSizeConfig = {
  avatar: SxProps<Theme>;
  icon: SystemStyleObject<Theme>;
  badge: BadgeConfig;
};

// Only small icons keep a non-scaling stroke; larger icons
// should scale their stroke proportionally with the rendered size.
const nonScalingStroke: SystemStyleObject<Theme> = {
  "& *[stroke]": { vectorEffect: "non-scaling-stroke" },
};

const avatarSizeConfig: Record<AppAvatarSize, AvatarSizeConfig> = {
  16: {
    avatar: {
      width: "16px",
      height: "16px",
      borderRadius: "6px",
      fontSize: "8px",
    },
    icon: { width: "12px", height: "12px", ...nonScalingStroke },
    badge: {
      avatar: {
        width: "8px",
        height: "8px",
        borderRadius: "2px",
        fontSize: "4px",
      },
      image: { width: "8px", height: "8px" },
      status: { width: "6px", height: "6px" },
    },
  },
  20: {
    avatar: {
      width: "20px",
      height: "20px",
      borderRadius: "8px",
      fontSize: "10px",
    },
    icon: { width: "12px", height: "12px", ...nonScalingStroke },
    badge: {
      avatar: {
        width: "10px",
        height: "10px",
        borderRadius: "4px",
        fontSize: "5px",
      },
      image: { width: "10px", height: "10px" },
      status: { width: "8px", height: "8px" },
    },
  },
  24: {
    avatar: {
      width: "24px",
      height: "24px",
      borderRadius: "8px",
      fontSize: "12px",
    },
    icon: { width: "16px", height: "16px", ...nonScalingStroke },
    badge: {
      avatar: {
        width: "12px",
        height: "12px",
        borderRadius: "4px",
        fontSize: "6px",
      },
      image: { width: "12px", height: "12px" },
      status: { width: "8px", height: "8px" },
    },
  },
  32: {
    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "12px",
      fontSize: "14px",
    },
    icon: { width: "20px", height: "20px", ...nonScalingStroke },
    badge: {
      avatar: {
        width: "16px",
        height: "16px",
        borderRadius: "6px",
        fontSize: "8px",
      },
      image: { width: "16px", height: "16px" },
      status: { width: "10px", height: "10px" },
    },
  },
  40: {
    avatar: {
      width: "40px",
      height: "40px",
      borderRadius: "16px",
      fontSize: "16px",
    },
    icon: { width: "24px", height: "24px", ...nonScalingStroke },
    badge: {
      avatar: {
        width: "16px",
        height: "16px",
        borderRadius: "6px",
        fontSize: "8px",
      },
      image: { width: "16px", height: "16px" },
      status: { width: "12px", height: "12px" },
    },
  },
  48: {
    avatar: {
      width: "48px",
      height: "48px",
      borderRadius: "20px",
      fontSize: "16px",
    },
    icon: { width: "28px", height: "28px" },
    badge: {
      avatar: {
        width: "20px",
        height: "20px",
        borderRadius: "8px",
        fontSize: "10px",
      },
      image: { width: "20px", height: "20px" },
      status: { width: "14px", height: "14px" },
    },
  },
  56: {
    avatar: {
      width: "56px",
      height: "56px",
      borderRadius: "24px",
      fontSize: "20px",
    },
    icon: { width: "32px", height: "32px" },
    badge: {
      avatar: {
        width: "20px",
        height: "20px",
        borderRadius: "8px",
        fontSize: "10px",
      },
      image: { width: "20px", height: "20px" },
      status: { width: "16px", height: "16px" },
    },
  },
  64: {
    avatar: {
      width: "64px",
      height: "64px",
      borderRadius: "24px",
      fontSize: "20px",
    },
    icon: { width: "40px", height: "40px" },
    badge: {
      avatar: {
        width: "24px",
        height: "24px",
        borderRadius: "8px",
        fontSize: "12px",
      },
      image: { width: "24px", height: "24px" },
      status: { width: "16px", height: "16px" },
    },
  },
};

export type AppAvatarColor =
  | "secondary"
  | "primary"
  | "accent"
  | "brand"
  | "info"
  | "success"
  | "warning"
  | "error";

const colorMap: Record<AppAvatarColor, SxProps<Theme>> = {
  secondary: {
    bgcolor: "custom.surfaceSecondary",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondary}`,
    color: "custom.icon",
  },
  primary: {
    bgcolor: "custom.fillEmphasis",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderEmphasis}`,
    color: "custom.textPrimary",
  },
  accent: {
    bgcolor: "custom.surfaceAccent",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondaryAccent}`,
    color: "custom.textAccent",
  },
  brand: {
    bgcolor: "custom.surfaceBrand",
    outline: (theme: Theme) => `1px solid ${theme.palette.custom.borderBrand}`,
    color: "custom.textBrand",
  },
  info: {
    bgcolor: "custom.surfaceInfo",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondaryInfo}`,
    color: "custom.textInfo",
  },
  success: {
    bgcolor: "custom.surfaceSuccess",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondarySuccess}`,
    color: "custom.textSuccess",
  },
  warning: {
    bgcolor: "custom.surfaceWarning",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondaryWarning}`,
    color: "custom.textWarning",
  },
  error: {
    bgcolor: "custom.surfaceError",
    outline: (theme: Theme) =>
      `1px solid ${theme.palette.custom.borderSecondaryError}`,
    color: "custom.textError",
  },
};

export type AppAvatarProps = {
  size?: AppAvatarSize;
  color?: AppAvatarColor;
  badge?: ReactNode;
} & Omit<AvatarProps, "variant">;

export const AppAvatar = forwardRef<HTMLDivElement, AppAvatarProps>(
  function AppAvatar(
    { size = 40, color, badge, sx, children, src, ...rest }: AppAvatarProps,
    ref,
  ) {
    const sizeConfig = avatarSizeConfig[size];

    const avatar = (
      <Avatar
        {...rest}
        ref={ref}
        src={src}
        data-slot="app-avatar"
        sx={[
          sizeConfig.avatar,
          {
            fontWeight: 600,
            border: "none",
            outline: (theme: Theme) =>
              `1px solid ${theme.palette.custom[src ? "borderImage" : "borderSecondary"]}`,
            outlineOffset: "-1px",
          },
          sizeConfig.icon && {
            "& svg[data-icon]": sizeConfig.icon,
          },
          color && colorMap[color],
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Avatar>
    );

    if (!badge) {
      return avatar;
    }

    if (!sizeConfig.badge) {
      return avatar;
    }

    return (
      <Box sx={{ position: "relative" }}>
        {avatar}
        <Box
          sx={{
            position: "absolute",
            bottom: -3,
            right: -3,
            display: "flex",
            "& [data-slot='app-avatar']": sizeConfig.badge.avatar,
            "& [data-slot='avatar-badge-image']": sizeConfig.badge.image,
            "& [data-slot='avatar-badge-status']": sizeConfig.badge.status,
          }}
        >
          {badge}
        </Box>
      </Box>
    );
  },
);
